/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { links } from "@octopusdeploy/portal-routes";
import InfrastructureRoutes from "app/areas/infrastructure/components/InfrastructureRoutes/index";
import LibraryRoutes from "app/areas/library/components/LibraryRoutes/index";
import TaskRoutes from "app/areas/tasks/components/TaskRoutes/index";
import TenantRoutes from "app/areas/tenants/TenantsRoutes/TenantsRoutes";
import * as React from "react";
import type { RouteComponentProps } from "react-router";
import { withRouter } from "react-router";
import { Route, Switch } from "react-router-dom";
import ConfigurationRoutes from "~/areas/configuration/components/ConfigurationRoutes/ConfigurationRoutes";
import { DashboardRoutes } from "~/areas/dashboard/DashboardRoutes/DashboardRoutes";
import DeploymentRoutes from "~/areas/deployments/DeploymentRoutes";
import InsightsRoutes from "~/areas/insights/components/InsightsRoutes/InsightsRoutes";
import { ProjectGroupPage } from "~/areas/projects/components/ProjectGroupLayout/ProjectGroupPage";
import { ProjectsRoutes } from "~/areas/projects/components/index";
import RunbookRunRoutes from "~/areas/runbookRuns/RunbookRunRoutes";
import UserProfileRoutes from "~/areas/users/UserProfileRoutes";
import { session } from "~/clientInstance";
import { NoPermissionsPage } from "~/components/NoPermissionsPage/NoPermissionsPage";
import { NotFound, RedirectAs404, stateFor404Redirect } from "~/components/NotFound/NotFound";
import { withPage } from "~/components/Page/WithPage";
import { useIsRoutingVNextEnabled } from "~/components/RootRoutes/useIsRoutingVNextEnabled";
import { isSpaceNotFound, isSpecificSpaceContext } from "~/components/SpaceLoader/SpaceLoader";
import type { SpaceContext } from "~/components/SpaceLoader/SpaceLoader";
import { StyleGuidePage } from "~/components/StyleGuide/StyleGuidePage";
import pageIds from "~/pageIds";
import { AllPageRoutes } from "~/routing/AllPageRoutes";
import routeLinks from "../../routeLinks";
import { MigratedReloadableRoute } from "../MigratedRoute/MigratedRoute";
import InternalRedirect from "../Navigation/InternalRedirect/InternalRedirect";
import { SpaceNotFound } from "../SpaceNotFound/SpaceNotFound";
import { ChannelRedirect, ReleaseRedirect, RunbookRedirect, TriggerRedirect } from "./ProjectChildRedirect";
import { RunbookSnapshotRedirect } from "./ProjectRunbookChildRedirect";
const StyleGuidePageWithPageDefinition = withPage({ page: pageIds.styleGuide })(StyleGuidePage);
const ProjectGroupPageWithPageDefinition = withPage({ page: pageIds.projectGroup.root })(ProjectGroupPage);
interface RootRoutesProps extends RouteComponentProps<{}> {
    spaceContext: SpaceContext;
}
export const typeSafeHasOwnProperty = <T extends {}>(target: T, key: keyof T) => {
    return target.hasOwnProperty(key);
};
function RootRoutes({ spaceContext }: RootRoutesProps) {
    const isRoutingVNextEnabled = useIsRoutingVNextEnabled();
    const noMatch = () => {
        if (isSpaceNotFound(spaceContext)) {
            return <MigratedReloadableRoute key="space not found" render={() => <SpaceNotFound spaceNotFoundContext={spaceContext}/>}/>;
        }
        else if (!session.currentPermissions!.hasAnyPermissions()) {
            return <MigratedReloadableRoute key="no permissions" component={NoPermissionsPage}/>;
        }
        return <RedirectAs404 key="404 redirect"/>;
    };
    const systemOrMixedScopeRoutes = () => {
        if (isSpaceNotFound(spaceContext)) {
            // If the space was not found, we don't want to show routes that are available in the system context.
            // We want to direct people to go to either a space-less route, or to step into a space.
            // So we should only show the Space Switcher in the navbar and disallow other routing.
            return [];
        }
        if (!session.currentPermissions!.hasAnyPermissions()) {
            return [];
        }
        return [
            <MigratedReloadableRoute key="tasks" path={routeLinks.tasks.root} component={TaskRoutes}/>,
            <MigratedReloadableRoute key="configuration" path={routeLinks.configuration.root} component={ConfigurationRoutes}/>,
            <MigratedReloadableRoute key="legacy" path={routeLinks.legacy.manage.console} render={(p) => <InternalRedirect to={{ ...p.location, pathname: routeLinks.tasks.console }}/>}/>,
        ];
    };
    const defaultRoute = () => {
        if (isSpaceNotFound(spaceContext)) {
            return [];
        }
        if (isSpecificSpaceContext(spaceContext)) {
            return [<MigratedReloadableRoute key="dashboard default" exact path={routeLinks.root} render={() => <InternalRedirect to={routeLinks.dashboard.root}/>}/>];
        }
        if (session.currentPermissions!.hasAnyPermissions()) {
            return [
                <MigratedReloadableRoute key="configuration redirect" exact path={routeLinks.root} render={() => {
                        return <InternalRedirect push={false} to={links.configurationRootRedirect.generateUrl()}/>;
                    }}/>,
            ];
        }
        return [];
    };
    const spaceRoutes = () => {
        if (!isSpecificSpaceContext(spaceContext)) {
            // If you are not in a space, then it does not make sense to render any space specific components
            // We are better off letting this fall through to a 404,
            // rather than try and fail to render a space specific component, and probably end up with undefined behaviour
            return [];
        }
        return [
            <MigratedReloadableRoute key="dashboard" path={routeLinks.dashboard.root} component={DashboardRoutes}/>,
            <MigratedReloadableRoute key="projects" path={routeLinks.projects.root} component={ProjectsRoutes}/>,
            <MigratedReloadableRoute key="project groups" path={routeLinks.projectGroup(":ProjectGroupId").root} render={(p: ProjectGroupRouteParams) => <ProjectGroupPageWithPageDefinition spaceId={p.match.params.spaceId} projectGroupId={p.match.params.ProjectGroupId}/>}/>,
            <MigratedReloadableRoute key="infrastructure" path={routeLinks.infrastructure.root} component={InfrastructureRoutes}/>,
            <MigratedReloadableRoute key="tenants" path={routeLinks.tenants} component={TenantRoutes}/>,
            <MigratedReloadableRoute key="library" path={routeLinks.library.root} component={LibraryRoutes}/>,
            <MigratedReloadableRoute key="deployments" path={routeLinks.deployments} component={DeploymentRoutes}/>,
            <MigratedReloadableRoute key="release redirect" path={routeLinks.releaseRedirect(":releaseId")} render={(p: RouteComponentProps<{
                spaceId: string;
                releaseId: string;
            }>) => <ReleaseRedirect spaceId={p.match.params.spaceId} releaseId={p.match.params.releaseId}/>}/>,
            <MigratedReloadableRoute key="trigger redirect" path={routeLinks.triggerRedirect(":triggerId")} render={(p: RouteComponentProps<{
                spaceId: string;
                triggerId: string;
            }>) => <TriggerRedirect spaceId={p.match.params.spaceId} triggerId={p.match.params.triggerId}/>}/>,
            <MigratedReloadableRoute key="channel redirect" path={routeLinks.channelRedirect(":channelId")} render={(p: RouteComponentProps<{
                spaceId: string;
                channelId: string;
            }>) => <ChannelRedirect spaceId={p.match.params.spaceId} channelId={p.match.params.channelId}/>}/>,
            <MigratedReloadableRoute key="runbookruns" path={routeLinks.runbookRuns} component={RunbookRunRoutes}/>,
            <MigratedReloadableRoute key="runbooksnapshot redirect" path={routeLinks.runbookSnapshotRedirect(":runbookSnapshotId")} render={(p: RouteComponentProps<{
                spaceId: string;
                runbookSnapshotId: string;
            }>) => <RunbookSnapshotRedirect spaceId={p.match.params.spaceId} runbookSnapshotId={p.match.params.runbookSnapshotId}/>}/>,
            <MigratedReloadableRoute key="runbook redirect" path={routeLinks.runbookRedirect(":runbookId")} render={(p: RouteComponentProps<{
                spaceId: string;
                runbookId: string;
            }>) => <RunbookRedirect spaceId={p.match.params.spaceId} runbookId={p.match.params.runbookId}/>}/>,
            <MigratedReloadableRoute key="legacy environments" path={routeLinks.legacy.environments} render={() => <InternalRedirect to={routeLinks.infrastructure.environments.root}/>}/>,
            <MigratedReloadableRoute key="legacy accounts" path={routeLinks.legacy.accounts} render={(routeProps) => <InternalRedirect to={{ pathname: routeLinks.infrastructure.accounts.root, search: routeProps.location.search }}/>}/>,
            <MigratedReloadableRoute key="legacy machines" path={routeLinks.legacy.machines} render={(routeProps) => <InternalRedirect to={{ pathname: routeLinks.infrastructure.machines.root, search: routeProps.location.search }}/>}/>,
            <MigratedReloadableRoute key="insights" path={routeLinks.insights.root} render={({ match }: RouteComponentProps<{
                spaceId: string;
            }>) => <InsightsRoutes spaceId={match.params.spaceId}/>}/>,
        ];
    };
    const legacyRoutes: React.ReactElement[] = [
        ...defaultRoute(),
        ...spaceRoutes(),
        <MigratedReloadableRoute key="current user" path={routeLinks.currentUser.me} component={UserProfileRoutes}/>,
        <MigratedReloadableRoute key="style guide" path={routeLinks.styleGuide} render={() => <StyleGuidePageWithPageDefinition />}/>,
        ...systemOrMixedScopeRoutes(),
        noMatch(),
    ];
    return (<Route render={({ location }) => {
            if (location.state === stateFor404Redirect) {
                return <NotFound />;
            }
            return isRoutingVNextEnabled ? <AllPageRoutes spaceContext={spaceContext}/> : <Switch>{legacyRoutes}</Switch>;
        }}/>);
}
export default withRouter(RootRoutes);
type ProjectGroupRouteParams = RouteComponentProps<{
    spaceId: string;
    ProjectGroupId: string;
}>;
